<script>
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB, per spec
const ALLOWED_EXTENSIONS = ['pdf', 'docx', 'txt', 'csv', 'md'];

export default {
  name: 'OnboardingKnowledgeBaseDropzone',
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    // Once files are staged the dropzone shrinks to an "add more" bar so the
    // staged file cards, not the empty-looking dropzone, hold the attention.
    compact: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isDragging: false,
    };
  },
  methods: {
    extensionOf(file) {
      return file.name.split('.').pop()?.toLowerCase();
    },
    validate(files) {
      const valid = [];
      const errors = [];

      Array.from(files).forEach(file => {
        const ext = this.extensionOf(file);
        if (!ALLOWED_EXTENSIONS.includes(ext)) {
          errors.push(
            this.$t('ONBOARDING_SHELL.STEPS.KNOWLEDGE_BASE.ERROR_TYPE', {
              name: file.name,
            })
          );
          return;
        }
        if (file.size > MAX_FILE_SIZE) {
          errors.push(
            this.$t('ONBOARDING_SHELL.STEPS.KNOWLEDGE_BASE.ERROR_SIZE', {
              name: file.name,
            })
          );
          return;
        }
        valid.push(file);
      });

      return { valid, errors };
    },
    handleFilesSelected(fileList) {
      if (this.disabled) return;
      const { valid, errors } = this.validate(fileList);
      if (valid.length) this.$emit('filesSelected', valid);
      errors.forEach(message => this.$emit('validationError', message));
    },
    handleInputChange(event) {
      this.handleFilesSelected(event.target.files);
      event.target.value = '';
    },
    handleDrop(event) {
      event.preventDefault();
      this.isDragging = false;
      this.handleFilesSelected(event.dataTransfer.files);
    },
    handleDragOver(event) {
      event.preventDefault();
      if (!this.disabled) this.isDragging = true;
    },
    handleDragLeave() {
      this.isDragging = false;
    },
    openFileBrowser() {
      if (this.disabled) return;
      this.$refs.fileInput.click();
    },
  },
};
</script>

<template>
  <div
    class="flex items-center justify-center border-2 border-dashed rounded-lg transition-colors"
    :class="[
      compact ? 'flex-row gap-2 px-4 py-3' : 'flex-col gap-2 p-8',
      isDragging
        ? 'border-woot-500 bg-woot-25 dark:bg-woot-900/20'
        : 'border-slate-200 dark:border-slate-700',
      disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
    ]"
    @click="openFileBrowser"
    @dragover="handleDragOver"
    @dragleave="handleDragLeave"
    @drop="handleDrop"
  >
    <template v-if="compact">
      <fluent-icon icon="add" size="16" class="text-woot-500" />
      <p class="mb-0 text-sm font-medium text-woot-500">
        {{ $t('ONBOARDING_SHELL.STEPS.KNOWLEDGE_BASE.ADD_MORE_FILES') }}
      </p>
    </template>
    <template v-else>
      <fluent-icon icon="document" size="24" class="text-slate-400" />
      <p class="text-sm text-slate-600 dark:text-slate-300">
        {{ $t('ONBOARDING_SHELL.STEPS.KNOWLEDGE_BASE.DROPZONE_LABEL') }}
      </p>
      <p class="text-xs text-slate-400">
        {{ $t('ONBOARDING_SHELL.STEPS.KNOWLEDGE_BASE.DROPZONE_HINT') }}
      </p>
    </template>
    <input
      ref="fileInput"
      type="file"
      multiple
      class="hidden"
      accept=".pdf,.docx,.txt,.csv,.md"
      @change="handleInputChange"
      @click.stop
    />
  </div>
</template>
